import { useMemo, useEffect } from 'react'
import { Controller, FieldValues, Path } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Select, Textfield } from '@clientbase/clientbase-library'
import {
  Box,
  Grid,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'

import { onlyNumber } from 'utils'
import {
  cycleIntervalBetween,
  FREQUENCY_ENUM_TO_NAME,
  FrequencyUnit,
  presetToPair
} from 'utils/frequency'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import { FrequencyControlsProps } from './FrequencyControls'

interface SelectOptions {
  value: number
  label: string
}

// Limite máximo de ciclos personalizados ("Outros").
// Cobrança/recorrência de cliente: backend limita a 120 (Recurrence model).
// Despesa recorrente (Contas a Pagar): backend não limita, autorizamos 15 anos.
export const MAX_CYCLES_CHARGE = 120
export const MAX_CYCLES_EXPENSE = 180

export enum Frequency {
  Semanal = 0,
  Mensal = 1,
  Trimestral = 2,
  Semestral = 3,
  Anual = 4,
  Quinzenal = 5,
  Bimestral = 6,
  Personalizada = 7
}

// Unidades da frequência personalizada (espelha o enum frequency_type do backend).
export const customFrequencyTypeOptions: { label: string; value: string }[] = [
  { label: 'Dias', value: 'days' },
  { label: 'Semanas', value: 'weeks' },
  { label: 'Meses', value: 'months' },
  { label: 'Anos', value: 'years' }
]

// Ordenado por duração do ciclo, não pela ordem numérica do enum
// (Quinzenal=5 e Bimestral=6 foram adicionados depois, mas devem aparecer
// entre Semanal/Mensal e Mensal/Trimestral respectivamente).
const frequencyOrder: Frequency[] = [
  Frequency.Semanal,
  Frequency.Quinzenal,
  Frequency.Mensal,
  Frequency.Bimestral,
  Frequency.Trimestral,
  Frequency.Semestral,
  Frequency.Anual,
  Frequency.Personalizada
]

export const chargeTypeOptions: SelectOptions[] = frequencyOrder.map(
  (value) => ({
    label: Frequency[value],
    value
  })
)

// Função para gerar as opções de frequência baseadas no tipo selecionado
const generateFrequencyOptions = (frequencyType: number): SelectOptions[] => {
  const frequencyLabels = {
    [Frequency.Semanal]: 'semanas',
    [Frequency.Quinzenal]: 'quinzenas',
    [Frequency.Mensal]: 'meses',
    [Frequency.Bimestral]: 'bimestres',
    [Frequency.Trimestral]: 'trimestres',
    [Frequency.Semestral]: 'semestres',
    [Frequency.Anual]: 'anos'
  }

  const suffix =
    frequencyLabels[frequencyType as keyof typeof frequencyLabels] || 'x'

  return [
    { label: 'Tempo indeterminado', value: 0 },
    { label: `2 ${suffix}`, value: 2 },
    { label: `3 ${suffix}`, value: 3 },
    { label: `4 ${suffix}`, value: 4 },
    { label: `5 ${suffix}`, value: 5 },
    { label: `6 ${suffix}`, value: 6 },
    { label: `7 ${suffix}`, value: 7 },
    { label: `8 ${suffix}`, value: 8 },
    { label: `9 ${suffix}`, value: 9 },
    { label: `10 ${suffix}`, value: 10 },
    { label: `11 ${suffix}`, value: 11 },
    { label: `12 ${suffix}`, value: 12 },
    { label: 'Outros', value: 13 }
  ]
}

export const chargeFrequencyOptions: SelectOptions[] = [
  { label: 'Tempo indeterminado', value: 0 },
  { label: '2x', value: 2 },
  { label: '3x', value: 3 },
  { label: '4x', value: 4 },
  { label: '5x', value: 5 },
  { label: '6x', value: 6 },
  { label: '7x', value: 7 },
  { label: '8x', value: 8 },
  { label: '9x', value: 9 },
  { label: '10x', value: 10 },
  { label: '11x', value: 11 },
  { label: '12x', value: 12 },
  { label: 'Outros', value: 13 }
]

/** Id fixo do aviso de elevação de ciclos, para não empilhar um por alteração. */
export const TOAST_CICLOS_ELEVADOS = 'frequency-controls-ciclos-elevados'
/** Id fixo do aviso de janela que ultrapassa o total, na edição (nada é alterado). */
export const TOAST_JANELA_EXCEDE_TOTAL =
  'frequency-controls-janela-excede-total'

function FrequencyControls<T extends FieldValues>({
  control,
  setStoredCycleValue,
  isUnique,
  watch,
  disabled,
  setValue,
  isChargeOrRecurrenceForm = false,
  isEditingRecurrence = false,
  hasPaidBillings = false,
  onUserManualSelection,
  onUserManualFrequency,
  onUserEditingCustomCycles
}: FrequencyControlsProps<T>) {
  const match = useMediaQuery('(max-width: 900px)', { noSsr: false })

  // Despesa recorrente aceita até 180 ciclos (15 anos); cobrança de cliente, 120.
  const maxCycles = isChargeOrRecurrenceForm
    ? MAX_CYCLES_CHARGE
    : MAX_CYCLES_EXPENSE

  // Get all items from the form
  const items = watch('items' as Path<T>) || []

  // Get the itemsWatch pattern from BodyModalNewChargeOrRecurrence
  const itemsFields = (items as any[]) || []
  const itemsWatch = watch(
    itemsFields
      .map((_, index) => [
        `items.${index}.price` as Path<T>,
        `items.${index}.quantity` as Path<T>,
        `items.${index}.description` as Path<T>,
        `items.${index}.product` as Path<T>
      ])
      .flat()
  )

  // A janela de ciclos do item entra no teto de totalCycles, então precisa ser observada:
  // sem isso o memo não recalculava ao mudar "A partir de qual ciclo?"/"Por quantos ciclos?".
  const itemsCycleWindowWatch = watch(
    itemsFields
      .map((_, index) => [
        `items.${index}.start_cycle` as Path<T>,
        `items.${index}.total_cycles` as Path<T>,
        `items.${index}.cycle_interval` as Path<T>
      ])
      .flat()
  )

  // Create a memoized analysis of the items
  const itemsAnalysis = useMemo(() => {
    if (!items || items.length === 0) {
      return {
        hasProducts: false,
        hasCustomItems: false,
        maxCycles: 0,
        minCycles: 0,
        cicloMinimoExigido: 0,
        itemQueExige: { label: '', inicio: 1, cobrancas: 0 },
        itensAteOFim: [] as string[],
        productsCount: 0,
        customItemsCount: 0
      }
    }

    let hasProducts = false
    let hasCustomItems = false
    let maxCycles = -1 // Inicializar com -1 para distinguir de 0 (infinito)
    let minCycles = -1 // Para rastrear o valor mínimo
    let productsCount = 0
    let customItemsCount = 0
    let hasInfiniteProduct = false // Flag para rastrear se há produto infinito
    // Piso exigido pelas janelas dos itens: o maior ciclo que alguma janela alcança,
    // e qual item o alcança (para o aviso ao merchant nomear o culpado).
    let cicloMinimoExigido = 0
    let itemQueExige = { label: '', inicio: 1, cobrancas: 0 }
    // Itens sem janela própria (total_cycles 0): cobrados em todo ciclo, então são os que
    // ganham cobrança extra quando o total da recorrência sobe.
    const itensAteOFim: string[] = []

    // Último ciclo em que a janela do item ainda é cobrada, espelhando
    // BillingItem.last_applicable_cycle no backend. Item infinito (total_cycles 0) não tem
    // fim próprio e devolve 0: segue o total da recorrência em vez de esticá-lo.
    const fimDaJanelaDoItem = (item: any): number => {
      const total = Number(item?.total_cycles) || 0
      if (total <= 0) return 0

      const inicio = Math.max(Number(item?.start_cycle) || 1, 1)
      const passo = Math.max(Number(item?.cycle_interval) || 1, 1)

      // Mesmo teto do backend (Recurrence#total_cycles aceita no máximo 120).
      return Math.min(inicio + (total - 1) * passo, MAX_CYCLES_CHARGE)
    }

    // Analyze each item
    items.forEach((item: any) => {
      // A janela configurada no item pode terminar depois do total de ciclos do produto:
      // start_cycle 2 com 3 cobranças vai até o ciclo 4 num plano de 3. Sem contar essa
      // janela aqui, o useEffect abaixo rebaixava totalCycles de volta para o teto do
      // produto e a última cobrança do item nunca era gerada.
      const fimDaJanela = fimDaJanelaDoItem(item)
      if (fimDaJanela > 0 && (maxCycles === -1 || fimDaJanela > maxCycles)) {
        maxCycles = fimDaJanela
      }
      const rotulo =
        item?.product?.label || item?.description?.trim() || 'Um dos itens'
      if (fimDaJanela > cicloMinimoExigido) {
        cicloMinimoExigido = fimDaJanela
        itemQueExige = {
          label: rotulo,
          inicio: Math.max(Number(item?.start_cycle) || 1, 1),
          cobrancas: Number(item?.total_cycles) || 0
        }
      }
      const temConteudo =
        item?.product?.value ||
        item?.description?.trim() ||
        Number(item?.price) > 0
      if (temConteudo && !(Number(item?.total_cycles) > 0)) {
        itensAteOFim.push(rotulo)
      }

      if (item && item.product && item.product.value) {
        // This is a product
        hasProducts = true
        productsCount++

        // Verificar se o produto tem a propriedade total_cycles
        let totalCycles

        // Tentar obter total_cycles de diferentes maneiras possíveis
        if (item.product.total_cycles !== undefined) {
          totalCycles = item.product.total_cycles
        } else if (
          item.product.product &&
          item.product.product.total_cycles !== undefined
        ) {
          totalCycles = item.product.product.total_cycles
        } else {
          if (isEditingRecurrence) {
            totalCycles = 0 // Tratar como infinito quando não conseguir determinar
          } else {
            totalCycles = 1 // Valor padrão apenas para novos produtos
          }
        }

        // Check if the product has total_cycles property
        if (totalCycles !== undefined) {
          // Se encontrarmos um produto com ciclos infinitos (0), marcar flag
          if (totalCycles === 0) {
            hasInfiniteProduct = true
          }
          // Para produtos finitos, calcular o máximo e mínimo
          else if (totalCycles > 0) {
            if (maxCycles === -1 || totalCycles > maxCycles) {
              maxCycles = totalCycles
            }
            if (minCycles === -1 || totalCycles < minCycles) {
              minCycles = totalCycles
            }
          }
        }
      } else if (item) {
        // Verificar se é um item avulso (sem produto selecionado)
        const hasNoProduct = !item.product || !item.product.value

        // Verificar se tem algum conteúdo preenchido
        const hasContent =
          // Verificar se há descrição preenchida
          (item.description && item.description.trim() !== '') ||
          // Verificar se há preço preenchido
          (item.price && item.price > 0) ||
          // Verificar se há valor digitado
          (item.typedValue && item.typedValue.trim() !== '')

        // Se não tem produto E tem algum conteúdo, é um item avulso válido
        if (hasNoProduct && hasContent) {
          // This is a custom item with either a description, price or typedValue
          hasCustomItems = true
          customItemsCount++
        }
      }
    })

    // Definir os valores finais baseado na análise
    let finalMaxCycles, finalMinCycles
    if (hasInfiniteProduct) {
      // Se há produto infinito, maxCycles = 0 (libera todas as opções)
      finalMaxCycles = 0
      finalMinCycles = 0
    } else if (maxCycles === -1) {
      // Se não há produtos ou não conseguiu calcular, usar 0 como padrão
      finalMaxCycles = 0
      finalMinCycles = 0
    } else {
      // Se só há produtos finitos, usar os valores calculados
      finalMaxCycles = maxCycles
      // CORREÇÃO: Para produtos, o mínimo sempre deve ser 2x, não o menor valor dos produtos
      finalMinCycles = 2
    }

    const result = {
      hasProducts,
      hasCustomItems,
      maxCycles: finalMaxCycles,
      minCycles: finalMinCycles,
      cicloMinimoExigido,
      itemQueExige,
      itensAteOFim,
      productsCount,
      customItemsCount
    }

    return result
  }, [items, itemsWatch, itemsCycleWindowWatch, isEditingRecurrence])

  // Get current frequency type to generate appropriate options
  const currentFrequency = watch('frequency' as Path<T>) as number

  // Generate dynamic frequency options based on selected frequency type
  const dynamicFrequencyOptions = useMemo(() => {
    return generateFrequencyOptions(currentFrequency)
  }, [currentFrequency])

  // Filter frequency options based on the current scenario
  const filteredFrequencyOptions = useMemo(() => {
    // Use dynamic options based on frequency type
    const baseOptions = dynamicFrequencyOptions

    // Case 1: Not in a charge/recurrence form or no products/items
    if (!isChargeOrRecurrenceForm) {
      return baseOptions
    }

    // Case 2: If there are any custom items - show all options
    if (itemsAnalysis.hasCustomItems) {
      return baseOptions
    }

    // Case 3: If there are no products - show all options
    if (!itemsAnalysis.hasProducts) {
      return baseOptions
    }

    // Case 4: If there are only products and the max cycles is 0 (infinite)
    // CORREÇÃO: Usar itemsAnalysis.maxCycles diretamente em vez de maxCyclesFromProducts
    // para evitar problemas de sincronização
    if (itemsAnalysis.maxCycles === 0) {
      // If infinite cycles, show ALL options (liberar todas as opções)
      return baseOptions
    }

    // Case 5: If there are only products (no custom items)
    // REGRA: Limitar as opções sendo o máximo delas o maior valor dos ciclos entre eles
    // Ex: se tem um produto de 2x, 6x e 11x, as opções vão estar limitadas entre 2x e 11x
    const maxCycles = itemsAnalysis.maxCycles
    const minCycles = itemsAnalysis.minCycles || 2 // Default para 2x se não definido

    const filteredOptions = baseOptions.filter((option) => {
      // CORREÇÃO: NÃO incluir "Tempo indeterminado" (0) quando há apenas produtos finitos
      // Só incluir se há produtos infinitos (que já foi tratado no Case 4 acima)
      if (option.value === 0) return false

      // Incluir opções dentro do range [minCycles, maxCycles]
      if (
        option.value >= minCycles &&
        option.value <= maxCycles &&
        option.value !== 13
      )
        return true

      // Incluir a opção "Outros" (13) somente se o máximo for maior que 12
      if (option.value === 13 && maxCycles > 12) return true

      return false
    })

    return filteredOptions
  }, [
    dynamicFrequencyOptions,
    isChargeOrRecurrenceForm,
    itemsAnalysis.hasCustomItems,
    itemsAnalysis.hasProducts,
    itemsAnalysis.maxCycles,
    itemsAnalysis.minCycles
  ])

  /**
   * Itens da composição cujo plano não cabe na frequência escolhida. Só tem conteúdo quando
   * o merchant troca a frequência à mão para uma que não serve ao que ele já escolheu — a
   * derivação automática nunca produz esse estado.
   *
   * Sem este aviso a incompatibilidade só apareceria no submit, como erro da API, e o
   * merchant não saberia qual item tirar.
   */
  const itensIncompativeis = useMemo(() => {
    const frequency = watch('frequency' as Path<T>) as number | undefined
    if (frequency === undefined || frequency === null) return []

    const preset = FREQUENCY_ENUM_TO_NAME[Number(frequency)] ?? 'monthly'
    const recurrencePair = presetToPair(
      preset,
      watch('frequency_type' as Path<T>) as FrequencyUnit | undefined,
      Number(watch('frequency_number' as Path<T>)) || undefined
    )

    const items = (watch('items' as Path<T>) as any[]) || []

    return items
      .filter((item) => {
        const p = item?.product
        if (!p?.value || p.value === 'custom' || !p.frequency) return false
        // Plano de cobrança única não impõe cadência — nunca conflita.
        if (p.total_cycles === 1 || p.total_cycles === undefined) return false

        return (
          cycleIntervalBetween(
            recurrencePair,
            presetToPair(
              p.frequency,
              p.frequency_type as FrequencyUnit | undefined,
              Number(p.frequency_number) || undefined
            )
          ) === null
        )
      })
      .map((item) => item?.product?.label || 'item sem nome')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watch('frequency' as Path<T>),
    watch('frequency_type' as Path<T>),
    watch('frequency_number' as Path<T>),
    items,
    // `watch('items')` devolve SEMPRE o mesmo array (o RHF muta em vez de
    // recriar), então escolher um plano não trocava a referência e o memo não
    // reavaliava: o aviso nunca aparecia e o merchant só descobria a
    // incompatibilidade ao salvar, sem saber qual item remover.
    // `itemsWatch` observa `items.N.product` campo a campo — é o mesmo
    // mecanismo que `itemsAnalysis` já usa logo acima.
    itemsWatch
  ])

  // Determine if frequency field should be disabled
  const isFrequencyDisabled = useMemo(() => {
    // Bloqueia alteração de frequência quando a recorrência já tem cobranças pagas
    if (isEditingRecurrence && hasPaidBillings) return true

    // Se estiver editando uma recorrência, não desabilitar o campo de frequência
    if (isEditingRecurrence) return false

    if (isUnique) return true
    // A frequência é DERIVADA dos planos escolhidos, mas continua editável: o merchant pode
    // querer uma frequência válida diferente da mínima (dois planos mensais cobrados
    // trimestralmente, por exemplo). Desabilitar por "ter produto" era o desenho antigo, em
    // que o plano ditava e não havia como discordar — e deixava o campo travado e vazio
    // quando a derivação não achava cadência.
    return false
  }, [isUnique, isEditingRecurrence, hasPaidBillings])

  // Determine if cycles field should be disabled
  const isCyclesDisabled = useMemo(() => {
    // Se estiver editando uma recorrência, não desabilitar o campo de ciclos
    if (isEditingRecurrence) return false

    if (isUnique) return true
    if (!isChargeOrRecurrenceForm) return false
    return false // Never disable the cycles field, even when only products are selected
  }, [isUnique, isChargeOrRecurrenceForm, isEditingRecurrence])

  /**
   * Eleva o total de ciclos para caber a janela configurada nos itens.
   *
   * A janela do item pode terminar depois do total da recorrência: começar no ciclo 2 e
   * durar 3 cobranças vai até o ciclo 4, mas a recorrência tem 3. O backend gera cobranças
   * até `total_cycles` e nada mais, então o ciclo 4 simplesmente não existia e o merchant
   * recebia 2 cobranças onde configurou 3.
   *
   * Quem grava o total é este formulário, então é aqui que a recorrência precisa nascer
   * com o tamanho certo. Só eleva, nunca reduz: encurtar é decisão do merchant, feita no
   * próprio campo (e o efeito abaixo cuida do teto).
   *
   * A elevação nunca é silenciosa. Ela tem um efeito colateral que o merchant precisa
   * avaliar: item sem janela própria (total_cycles 0, "até o fim") é cobrado em TODO ciclo
   * da recorrência (BillingItem#applies_to_cycle?), então esticar o total também estica
   * esses itens. Com 3 ciclos e um item de R$100 até o fim, elevar para 4 gera uma 4ª
   * cobrança de R$100 que ninguém configurou. O toast diz o que mudou e por quê, para o
   * merchant reverter no próprio campo se preferir encurtar a janela do item.
   *
   * Na EDIÇÃO de uma recorrência viva a elevação não acontece. Lá o efeito colateral é
   * pior: o batch do Stateful materializa os ciclos novos numa recorrência em andamento, e
   * o cliente recebe cobranças a mais sem ninguém ter pedido. Nesse caso só avisa que a
   * janela ultrapassa o total (e que o excedente não será cobrado) e deixa a decisão com o
   * merchant: aumentar o total ou encurtar a janela.
   */
  useEffect(() => {
    if (!isChargeOrRecurrenceForm || isUnique) return

    const exigido = itemsAnalysis.cicloMinimoExigido
    if (!exigido || exigido > MAX_CYCLES_CHARGE) return

    const currentTotalCycles = watch('totalCycles' as Path<T>)
    const currentTotalCyclesCustom = watch('totalCyclesCustom' as Path<T>)

    // "Outros" (13) guarda o número real em totalCyclesCustom.
    const totalAtual =
      currentTotalCycles === 13
        ? parseInt(currentTotalCyclesCustom || '0', 10)
        : Number(currentTotalCycles) || 0

    // Recorrência infinita (0) já cobre qualquer janela.
    if (totalAtual === 0 || totalAtual >= exigido) return

    if (isEditingRecurrence) {
      const naoCobradas = exigido - totalAtual
      const aviso = (
        <Box data-testid="aviso-janela-excede-total">
          <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
            Janela do item ultrapassa o total de ciclos
          </Typography>
          <Typography sx={{ fontSize: '13px', mt: '4px' }}>
            &quot;{itemsAnalysis.itemQueExige.label}&quot; vai até o ciclo{' '}
            {exigido}, mas a recorrência tem {totalAtual}.{' '}
            {naoCobradas === 1
              ? 'A última cobrança dele não será gerada.'
              : `As ${naoCobradas} últimas cobranças dele não serão geradas.`}
          </Typography>
          <Typography sx={{ fontSize: '13px', mt: '4px' }}>
            Aumente o total de ciclos ou encurte a janela do item.
          </Typography>
        </Box>
      )
      if (toast.isActive(TOAST_JANELA_EXCEDE_TOTAL)) {
        toast.update(TOAST_JANELA_EXCEDE_TOTAL, { render: aviso })
      } else {
        toast.warning(aviso, {
          ...toastProps,
          autoClose: false,
          toastId: TOAST_JANELA_EXCEDE_TOTAL
        })
      }
      return
    }

    if (exigido <= 12) {
      setValue?.('totalCycles' as Path<T>, exigido as any)
      setValue?.('totalCyclesCustom' as Path<T>, undefined as any)
    } else {
      setValue?.('totalCycles' as Path<T>, 13 as any)
      setValue?.('totalCyclesCustom' as Path<T>, exigido.toString() as any)
    }

    // Um toastId fixo evita empilhar um aviso por tecla digitada em "Por quantos
    // ciclos?": enquanto o aviso estiver na tela, só o conteúdo é atualizado. Sem
    // fechamento automático: é uma consequência financeira, não pode sumir sozinha.
    const aviso = (
      <Box data-testid="aviso-ciclos-elevados">
        <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
          Ciclos ajustados de {totalAtual} para {exigido}
        </Typography>
        <Typography sx={{ fontSize: '13px', mt: '4px' }}>
          &quot;{itemsAnalysis.itemQueExige.label}&quot; começa no ciclo{' '}
          {itemsAnalysis.itemQueExige.inicio} e cobra{' '}
          {itemsAnalysis.itemQueExige.cobrancas}{' '}
          {itemsAnalysis.itemQueExige.cobrancas === 1 ? 'vez' : 'vezes'}, então
          vai até o ciclo {exigido}.
        </Typography>
        {itemsAnalysis.itensAteOFim.length > 0 && (
          <Typography sx={{ fontSize: '13px', mt: '4px' }}>
            {itemsAnalysis.itensAteOFim.length === 1 ? 'Item' : 'Itens'} até o
            fim ({itemsAnalysis.itensAteOFim.join(', ')}){' '}
            {itemsAnalysis.itensAteOFim.length === 1
              ? 'também será cobrado'
              : 'também serão cobrados'}{' '}
            {exigido - totalAtual === 1
              ? `no ciclo ${exigido}`
              : `nos ciclos ${totalAtual + 1} a ${exigido}`}
            .
          </Typography>
        )}
      </Box>
    )
    if (toast.isActive(TOAST_CICLOS_ELEVADOS)) {
      toast.update(TOAST_CICLOS_ELEVADOS, { render: aviso })
    } else {
      toast.warning(aviso, {
        ...toastProps,
        autoClose: false,
        toastId: TOAST_CICLOS_ELEVADOS
      })
    }
  }, [
    isChargeOrRecurrenceForm,
    isUnique,
    itemsAnalysis.cicloMinimoExigido,
    itemsAnalysis.itemQueExige,
    itemsAnalysis.itensAteOFim,
    isEditingRecurrence,
    watch,
    setValue
  ])

  // useEffect para ajustar automaticamente o valor de totalCycles quando necessário
  useEffect(() => {
    // Só aplicar a lógica se estivermos em um formulário de cobrança/recorrência
    if (!isChargeOrRecurrenceForm || isUnique) return

    // Só aplicar se há produtos e não há itens customizados
    if (!itemsAnalysis.hasProducts || itemsAnalysis.hasCustomItems) return

    // Só aplicar se o maxCycles não for 0 (infinito)
    if (itemsAnalysis.maxCycles === 0) return

    const currentTotalCycles = watch('totalCycles' as Path<T>)
    const currentTotalCyclesCustom = watch('totalCyclesCustom' as Path<T>)

    // Se o valor atual for "Outros" (13), verificar o valor customizado
    if (currentTotalCycles === 13) {
      const customValue = parseInt(currentTotalCyclesCustom || '0', 10)
      if (customValue > itemsAnalysis.maxCycles) {
        // Se o valor customizado for maior que o máximo permitido, ajustar para o máximo
        if (itemsAnalysis.maxCycles <= 12) {
          // Se o máximo for <= 12, usar o valor direto
          setValue?.('totalCycles' as Path<T>, itemsAnalysis.maxCycles as any)
          setValue?.('totalCyclesCustom' as Path<T>, undefined as any)
        } else {
          // Se o máximo for > 12, manter em "Outros" mas ajustar o valor customizado
          setValue?.(
            'totalCyclesCustom' as Path<T>,
            itemsAnalysis.maxCycles.toString() as any
          )
        }
      }
    } else if (currentTotalCycles > itemsAnalysis.maxCycles) {
      // Se o valor atual for maior que o máximo permitido pelos produtos
      if (itemsAnalysis.maxCycles <= 12) {
        // Se o máximo for <= 12, usar o valor direto
        setValue?.('totalCycles' as Path<T>, itemsAnalysis.maxCycles as any)
        setValue?.('totalCyclesCustom' as Path<T>, undefined as any)
      } else {
        // Se o máximo for > 12, usar "Outros" com o valor customizado
        setValue?.('totalCycles' as Path<T>, 13 as any)
        setValue?.(
          'totalCyclesCustom' as Path<T>,
          itemsAnalysis.maxCycles.toString() as any
        )
      }
    }
  }, [
    isChargeOrRecurrenceForm,
    isUnique,
    itemsAnalysis.hasProducts,
    itemsAnalysis.hasCustomItems,
    itemsAnalysis.maxCycles,
    watch,
    setValue
  ])

  // Verificar apenas se estamos no modo único (isUnique)
  // Não precisamos mais verificar o valor de totalCycles

  // Só retorna null se estiver no modo único (isUnique = true)
  // Não retorna null se totalCycles = 1 mas isUnique = false (modo recorrente)
  if (isUnique) {
    return null
  }

  return (
    <Stack gap={2} mt={1}>
      <Typography
        variant="mdBold"
        color={
          isFrequencyDisabled
            ? theme.palette.neutral[500]
            : theme.palette.neutral[200]
        }
      >
        Frequência e ciclos
      </Typography>

      {/* `flex-start` e não `end`: alinhado pela base, a mensagem de erro que
          nasce embaixo do seletor de frequência empurrava o próprio seletor
          para cima e ele saía do lugar em relação ao "por" e ao de ciclos. Pelo
          topo, o erro só ocupa o espaço abaixo e os campos ficam parados. */}
      <Grid
        container={true}
        rowSpacing={1}
        columnSpacing={match ? 1 : 2}
        alignItems="flex-start"
      >
        <Grid item={true} xs={12} sm={6} lg={6}>
          <Controller
            control={control}
            name={'frequency' as Path<T>}
            render={({ fieldState: { error }, field }) => (
              <Tooltip
                title={
                  isEditingRecurrence && hasPaidBillings
                    ? 'Não é possível alterar a frequência: a recorrência já possui cobranças pagas.'
                    : ''
                }
                placement="top"
                arrow
              >
                <span>
                  <Select
                    helperText={error?.message}
                    error={!!error?.message}
                    fullWidth
                    // `?? ''` mantém o Select CONTROLADO desde o primeiro render. Sem
                    // default, field.value começa undefined; o MUI trata isso como campo
                    // não-controlado e passa a ignorar o valor que chega depois — a
                    // frequência era derivada corretamente e o campo continuava em branco.
                    value={field.value ?? ''}
                    displayEmpty
                    disabled={isFrequencyDisabled || disabled}
                    onChange={(event) => {
                      field.onChange(event)
                      // A partir daqui a frequência é do merchant: a derivação para de
                      // sobrescrever, senão a escolha manual é revertida no render seguinte
                      // e a mensagem de incompatibilidade pisca e some.
                      onUserManualFrequency?.()
                      // O preset é um atalho de UI que preenche o par canônico
                      // (frequency_type, frequency_number). Personalizada apenas
                      // semeia o painel; o merchant escolhe o intervalo.
                      if (!setValue) return

                      const preset =
                        FREQUENCY_ENUM_TO_NAME[Number(event.target.value)]
                      if (!preset) return

                      const [unit, count] =
                        preset === 'custom'
                          ? [
                              watch('frequency_type' as Path<T>) ?? 'months',
                              watch('frequency_number' as Path<T>) ?? 1
                            ]
                          : presetToPair(preset)

                      setValue('frequency_type' as Path<T>, unit as any)
                      setValue('frequency_number' as Path<T>, count as any)
                    }}
                  >
                    {chargeTypeOptions.map((option) => (
                      <MenuItem key={option.label} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              </Tooltip>
            )}
          />
          {/* O conflito aparece onde ele foi criado — ao lado do campo que o merchant
              acabou de mudar — e nomeia os itens, que é o que ele precisa para decidir
              entre voltar a frequência ou tirar o item. */}
          {itensIncompativeis.length > 0 && (
            <Typography
              variant="xsLight"
              color={theme.palette.error?.main || '#EF4444'}
              sx={{ mt: 0.5, display: 'block' }}
            >
              {itensIncompativeis.length === 1
                ? `O item "${itensIncompativeis[0]}" não tem datas compatíveis com esta frequência.`
                : `${
                    itensIncompativeis.length
                  } itens não têm datas compatíveis com esta frequência: ${itensIncompativeis.join(
                    ', '
                  )}.`}
            </Typography>
          )}
        </Grid>
        <Grid
          item={true}
          xs={12}
          sm={0.5}
          lg={0.5}
          justifySelf="center"
          textAlign={match ? 'left' : 'center'}
          // Altura do input (não do meio da linha): com o container alinhado
          // pelo topo, sem isto o "por" subiria para a borda superior.
          sx={{ paddingTop: { sm: '14px' } }}
        >
          <Typography
            variant="smLight"
            color={isFrequencyDisabled ? theme.palette.neutral[500] : 'inherit'}
          >
            por
          </Typography>
        </Grid>
        <Grid
          item={true}
          xs={12}
          sm={watch('totalCycles' as Path<T>) === 13 ? 2.8 : 5.5}
          lg={watch('totalCycles' as Path<T>) === 13 ? 2.8 : 5.5}
        >
          <Controller
            control={control}
            name={'totalCycles' as Path<T>}
            render={({ fieldState: { error }, field }) => (
              <Select
                helperText={error?.message}
                error={!!error?.message}
                fullWidth
                value={field.value ?? ''}
                disabled={isCyclesDisabled || disabled}
                displayEmpty
                onChange={(event) => {
                  const newValue = event.target.value as number
                  field.onChange(event)
                  // Notify parent component that user has manually selected a value
                  onUserManualSelection?.()

                  // Se o valor selecionado for 13 (Outros), garantir que totalCyclesCustom seja definido
                  if (newValue === 13) {
                    // Se não houver valor em totalCyclesCustom, definir um valor padrão (14)
                    const currentCustomValue = watch(
                      'totalCyclesCustom' as Path<T>
                    )
                    if (!currentCustomValue && setValue) {
                      setValue('totalCyclesCustom' as Path<T>, '14' as any)
                    }
                  }

                  !!setStoredCycleValue && setStoredCycleValue(newValue)
                }}
                renderValue={(value) => {
                  if (!value) return 'Tempo indeterminado'
                  if (value === 13) return 'Outros'

                  // Get the appropriate suffix based on frequency type
                  const frequencyLabels = {
                    [Frequency.Semanal]: 'semanas',
                    [Frequency.Quinzenal]: 'quinzenas',
                    [Frequency.Mensal]: 'meses',
                    [Frequency.Bimestral]: 'bimestres',
                    [Frequency.Trimestral]: 'trimestres',
                    [Frequency.Semestral]: 'semestres',
                    [Frequency.Anual]: 'anos'
                  }

                  const suffix =
                    frequencyLabels[
                      currentFrequency as keyof typeof frequencyLabels
                    ] || 'x'
                  return `${value} ${suffix}`
                }}
              >
                {filteredFrequencyOptions.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Grid>
        {watch('totalCycles' as Path<T>) === 13 && (
          <Grid
            item={true}
            xs={12}
            sm={2.7}
            lg={2.7}
            textAlign={match ? 'left' : 'center'}
            display="flex"
            justifyContent="end"
          >
            <Controller
              control={control}
              name={'totalCyclesCustom' as Path<T>}
              render={({ fieldState: { error }, field }) => (
                <Textfield
                  disabled={isUnique || disabled}
                  helperText={error?.message}
                  name="totalCyclesCustom"
                  onFocus={() => {
                    // Quando o usuário foca no campo, marca que está editando
                    onUserEditingCustomCycles?.(true)
                  }}
                  onBlur={() => {
                    // Quando o usuário sai do campo, marca que não está mais editando
                    onUserEditingCustomCycles?.(false)
                  }}
                  FormHelperTextProps={{
                    sx: {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      color: theme.palette.error.main,
                      fontSize: '0.75rem',
                      mt: '2px'
                    }
                  }}
                  onChange={(event) => {
                    // Aceita o que o usuário digitar (só dígitos). A validação de
                    // máximo fica no schema Yup, que mostra a mensagem com o limite
                    // em vez de descartar o valor silenciosamente.
                    const res = onlyNumber(event.target.value)
                    field.onChange(res)
                  }}
                  value={field.value ?? ''}
                  placeholder={`Máx. ${maxCycles}`}
                  error={!!error?.message}
                />
              )}
            />
          </Grid>
        )}
        {currentFrequency === Frequency.Personalizada && (
          <Grid item={true} xs={12}>
            {/* Bloco fixo, não accordion: nasce sempre aberto e tem só dois
                campos, então o chevron sugeria um recolher que não existia e a
                moldura do AccordionSummary desalinhava o "A cada" dos inputs.
                Sem título próprio: o select logo acima já diz "Personalizada" e
                a própria linha se lê como "A cada 1 mês(es)".
                "A cada" fica alinhado à linha de base dos inputs (não ao label
                acima deles), e as duas colunas têm a mesma largura. */}
            <Box
              sx={{
                mt: 0.5,
                padding: '12px 16px 14px',
                border: `1px solid ${theme.palette.neutral[700]}`,
                borderRadius: '8px'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                gap={2}
              >
                <Typography
                  variant="smLight"
                  sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    paddingBottom: { xs: 0, sm: '10px' }
                  }}
                >
                  A cada
                </Typography>

                <Box flex={1} minWidth={0}>
                  <Controller
                    control={control}
                    name={'frequency_number' as Path<T>}
                    render={({ fieldState: { error }, field }) => (
                      <Textfield
                        label="Quantidade"
                        fullWidth
                        helperText={error?.message}
                        error={!!error?.message}
                        disabled={isFrequencyDisabled || disabled}
                        value={field.value ?? ''}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => field.onChange(onlyNumber(event.target.value))}
                      />
                    )}
                  />
                </Box>

                <Box flex={1} minWidth={0}>
                  <Controller
                    control={control}
                    name={'frequency_type' as Path<T>}
                    render={({ fieldState: { error }, field }) => (
                      <Select
                        label="Unidade"
                        fullWidth
                        displayEmpty
                        helperText={error?.message}
                        error={!!error?.message}
                        disabled={isFrequencyDisabled || disabled}
                        value={field.value ?? 'months'}
                        onChange={(event) => field.onChange(event)}
                      >
                        {customFrequencyTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Box>
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>
    </Stack>
  )
}

export default FrequencyControls
